// Path -> route config. In one bundle, the path decides which company application renders.
//
// Company application routes are DERIVED from the application catalog (applications/applicationCatalog.js)
// — the per-company definitions live there, not here. This module only maps an application's `type` to the
// component surface that renders it and adds the platform routes (/demo, the /portal landing redirect).
// Permission logic (who may open what, where users land) lives in access/permissions.js and is re-exported
// below for existing callers.
import { catalog } from '../applications/applicationCatalog';

export {
  getGroups, canAccess, homePathForGroups,
} from '../access/permissions';

// Application type -> the surface App.js renders (plus surface-specific flags).
const SURFACE_FOR_TYPE = {
  operations: { surface: 'ops', isAdmin: true },
  dispatch: { surface: 'ops', isAdmin: false },
  driver: { surface: 'driver' },
  'booking-chat': { surface: 'ecapchat', alwaysPublic: true },
  'agent-chat': { surface: 'chat' },
  'charter-dispatch': { surface: 'dispatch' },
  'charter-config': { surface: 'config' },
  // Operator-facing charter inquiry inbox (Outlook via Graph) running a Studio flow per thread.
  'charter-mail': { surface: 'chartermail' },
};

// Chat pages take their brand in the shape CustomerApp / EcapChatPage already read.
function chatBrand(app) {
  const b = app.branding || {};
  return b.isCharter
    ? { isCharter: true, brandName: b.companyName, logoSrc: b.logoSrc || undefined, subtitle: b.subtitle }
    : { isCharter: false, brandName: undefined, logoSrc: undefined, subtitle: b.subtitle };
}

function routeFor(app) {
  const s = SURFACE_FOR_TYPE[app.type];
  if (!s) return null;
  const cfg = {
    product: app.tenantId,
    appId: app.appId,
    identityPool: app.access.identityPool,
    ...s,
    requiredGroups: app.access.anyOfGroups,
  };
  // An application marked public opens with no login on every host (App.js), whatever its type.
  if (app.access.public) cfg.alwaysPublic = true;
  if (app.type === 'agent-chat') {
    cfg.teamId = app.runtime.teamId;
    cfg.agentId = app.runtime.agentId || '';
    cfg.brand = chatBrand(app);
  }
  if (app.type === 'charter-mail') {
    // The flow's PUBLIC runtime key. Kept out of the application definition so no screen can render
    // it (applicationCatalog.test.js asserts this), and attached here for the page to start sessions.
    cfg.flowKey = catalog.runtimeKeyFor(app.appId);
    cfg.brand = chatBrand(app);
  }
  if (app.type === 'booking-chat') {
    // The flow's PUBLIC chat key (build-time env, or a ?key= override in EcapChatPage). Kept out of the
    // application definition so no screen can render it.
    cfg.flowKey = catalog.runtimeKeyFor(app.appId);
    cfg.brand = chatBrand(app);
  }
  return Object.freeze(cfg);
}

const ROUTES = {};
catalog.listApplications().forEach((app) => {
  const cfg = routeFor(app);
  if (cfg) ROUTES[app.route] = cfg;
});
// Deploy Demo: a shareable, anonymous preview of any Studio flow, addressed by the demo token in ?key=.
// Always public — a demo link must open for whoever it was sent to, even on a login-gated dev host.
ROUTES['/demo'] = Object.freeze({ product: 'demo', surface: 'demo', alwaysPublic: true, requiredGroups: [] });
// The retired /portal launcher. Not an application: after sign-in App.js sends the user to their landing page
// (/studio for a super-admin, /r3ops for an R3 admin, …). Requires a login, so it never falls back to the
// public customer chat the way an unknown path does.
ROUTES['/portal'] = Object.freeze({ product: 'platform', surface: 'landing', requiredGroups: [] });

// Old paths keep working (rendered in place, so bookmarks don't break). '/' is the default landing.
export const LEGACY_ALIASES = Object.freeze({ '/': '/r3chat', ...catalog.aliases() });

/** Normalize a raw pathname to its canonical route path (lower-cased, no trailing slash, aliased). */
export function canonicalPath(rawPath) {
  const p = (rawPath || '/').toLowerCase().replace(/\/+$/, '') || '/';
  return LEGACY_ALIASES[p] || p;
}

/** The route config for a path, or null if the path isn't a known route. */
export function resolveProduct(rawPath) {
  return ROUTES[canonicalPath(rawPath)] || null;
}

/** True for a route that only exists to send a signed-in user to their landing page. */
export function isLandingRoute(cfg) {
  return !!cfg && cfg.surface === 'landing';
}
